import {
  CountryConfiguration,
  CountryConfigurationUrls,
  useApiCountryConfigurationCurrentRetrieve,
  useApiCountryConfigurationNavigationRetrieve,
} from '../../../apiClient/latest';
import { ErrorType } from '../../Network/orvalHTTPInterface';

const METERS_IN_MILE = 1609.34;
const METERS_IN_KM = 1000;

// UTILS //

export const isEU = (configuration?: CountryConfiguration): boolean => {
  if (!configuration) return false;
  return configuration?.continentCode === 'EU';
};

export const convertLocalizedToMeters = (
  value: number,
  configuration?: CountryConfiguration
): number => {
  if (!configuration || configuration.distanceUnit === 'mi') {
    return Math.round(value * METERS_IN_MILE);
  }
  if (configuration.distanceUnit === 'km') {
    return value * METERS_IN_KM;
  }
  return value;
};

export const convertMetersToLocalized = (
  value: number,
  configuration?: CountryConfiguration
): number => {
  // Round values to nearest 10th, accounting for floating point errors
  if (!configuration || configuration.distanceUnit === 'mi') {
    return Math.round((value / METERS_IN_MILE + Number.EPSILON) * 10) / 10;
  }
  if (configuration.distanceUnit === 'km') {
    return Math.round((value / METERS_IN_KM + Number.EPSILON) * 10) / 10;
  }
  return value;
};

// HOOKS //

export type UseCurrentCountryConfiguration = ReturnType<
  typeof useApiCountryConfigurationCurrentRetrieve
> & {
  data?: CountryConfiguration;
  error: ErrorType<Error> | null;
  emergencyHotline: string | undefined;
  urls: CountryConfigurationUrls | undefined;
};

export const useCurrentCountryConfiguration = ({
  enabled = true,
}: Partial<{ enabled: boolean }> = {}): UseCurrentCountryConfiguration => {
  const query = useApiCountryConfigurationCurrentRetrieve({ query: { enabled } });
  const emergencyHotline = query.data?.phoneNumbers?.emergencyHotlinePhoneFormatted;
  const urls = query.data?.urls;
  return { ...query, emergencyHotline, urls };
};

export type UseNavigationCountryConfiguration = ReturnType<
  typeof useApiCountryConfigurationCurrentRetrieve
> & {
  data?: CountryConfiguration;
  error: ErrorType<Error> | null;
  availableLanguages: string[][] | undefined;
  isEU: boolean;
  urls: CountryConfigurationUrls | undefined;
};

export const useNavigationCountryConfiguration = ({
  enabled = true,
}): UseNavigationCountryConfiguration => {
  const query = useApiCountryConfigurationNavigationRetrieve({ query: { enabled } });
  const availableLanguages = query.data?.availableLanguages;
  const isEurope = isEU(query.data);
  const urls = query.data?.urls;
  return { ...query, availableLanguages, isEU: isEurope, urls };
};
